import { IResponseProcessor } from "..";

/**
 * @deprecated Use RTK Query hooks from randmar-api-client instead.
 */
export default class ManufacturersApi {
  constructor(
    private _baseUrl: string,
    private _getToken: (applicationId: string) => string,
    private _processor: IResponseProcessor
  ) { }

  private async _fetchAndJson<T>(applicationId: string, path: string, options: RequestInit = {}): Promise<T> {
    const res = await fetch(`${this._baseUrl}${path}`, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${this._getToken(applicationId)}`,
      },
    });

    this._processor.process(res);

    return res.json();
  }

  private _postReport(applicationId: string, reportPath: string, params: Record<string, string>): Promise<boolean> {
    const searchParams = new URLSearchParams(params);
    return this._fetchAndJson(
      applicationId,
      `/Manufacturer/${applicationId}/Report${reportPath}?${searchParams.toString()}`,
      { method: "POST" }
    );
  }

  /**
   * @deprecated Use `useGetV4ManufacturerByRouteManufacturerIdAccountQuery` RTK Query hook instead.
   */
  async retrieveInfo(applicationId: string): Promise<IManufacturerInfo> {
    return this._fetchAndJson<IManufacturerInfo>(applicationId, `/Manufacturer/${applicationId}/Account`);
  }

  /**
   * @deprecated Use `useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery` RTK Query hook instead.
   */
  async retrieveInventoryValue(applicationId: string): Promise<IInventoryValue[]> {
    return this._fetchAndJson<IInventoryValue[]>(applicationId, `/Manufacturer/${applicationId}/Inventory/Value`);
  }

  /**
   * @deprecated Use `usePostV4PartnerByApplicationIdAccountManufacturerAndFieldNameMutation` RTK Query hook instead.
   */
  async updateManufacturerProperty(applicationId: string, fieldId: string, newValue: string): Promise<boolean> {
    const params = new URLSearchParams({ newValue });
    return this._fetchAndJson(
      applicationId,
      `/Manufacturer/${applicationId}/Account/${fieldId}?${params.toString()}`,
      { method: "POST" }
    );
  }

  /**
   * @deprecated Use specific report download mutation hooks from randmar-api-client, e.g., `usePostV4ManufacturerByRouteManufacturerIdReportReorderEmailMutation`.
   */
  async downloadReport(applicationId: string, type: string, email: string): Promise<boolean> {
    return this._postReport(applicationId, `/${type}/Email`, { emailAddress: email });
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdReportSalesAndResellerIdEmailMutation` RTK Query hook instead.
   */
  async downloadResellerSales(applicationId: string, resellerId: string, email: string, dateStart: string, dateEnd: string): Promise<boolean> {
    return this._postReport(applicationId, `/Sales/${resellerId}/Email`, {
      emailAddress: email,
      startDate: dateStart,
      endDate: dateEnd,
    });
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdReportSalesEmailMutation` RTK Query hook instead.
   */
  async downloadSales(applicationId: string, email: string, dateStart: string, dateEnd: string): Promise<boolean> {
    return this._postReport(applicationId, "/Sales/Email", {
      emailAddress: email,
      startDate: dateStart,
      endDate: dateEnd,
    });
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdReportClaimEmailMutation` RTK Query hook instead.
   */
  async downloadClaim(applicationId: string, email: string, dateStart: string, dateEnd: string): Promise<boolean> {
    return this._postReport(applicationId, "/Claim/Email", {
      emailAddress: email,
      startDate: dateStart,
      endDate: dateEnd,
    });
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdReportInventoryEmailMutation` RTK Query hook instead.
   */
  async downloadInventory(applicationId: string, email: string, inventoryDate: string): Promise<boolean> {
    return this._postReport(applicationId, "/Inventory/Email", {
      emailAddress: email,
      inventoryDate,
    });
  }

  /**
   * @deprecated Use `usePostV4PartnerByApplicationIdAccountFeedbackMutation` RTK Query hook instead.
   */
  async sendManufacturerFeedback(applicationId: string, emailAddress: string, comment: string): Promise<boolean> {
    const params = new URLSearchParams({
      emailAddress,
      comment,
    });
    return this._fetchAndJson(
      applicationId,
      `/Partner/${applicationId}/Account/Feedback?${params.toString()}`,
      { method: "POST" }
    );
  }
}

export interface IManufacturerInfo {
  Address1: string;
  Address2: string;
  AREmail: string;
  AutoReorderDate: number;
  AutoUpdate: boolean;
  City: string;
  ClaimEmail: string;
  ContactName: string;
  CreationDate: number;
  CreditLimit: number;
  CurrencyCode: string;
  DefaultLeadTime: string;
  DefaultMaxInventoryValue: number;
  DefaultMinimumOrder: number;
  DefaultTaxGroup: string;
  DefaultWeeksOfStocking: string;
  Domain: string;
  FiscalYearMonthStart: number;
  GoogleEmail: string;
  ManufacturerId: string;
  Name: string;
  OnHold: number;
  OutstandingBalance: number;
  PartnerPortal: string;
  Phone: string;
  POEmail: string;
  PostalCode: string;
  Province: string;
  PublicEmail: string;
  PublicName: string;
  RepresentativeEmail: string;
  SalesData: ISalesData;
  ShopifyWebsite: string;
  Terms: string;
  Website: string;
}

export interface IInventoryValue {
  Category: string;
  InventoryValue: number;
  Location: string;
  PurchaseOrderValue: number;
  SalesOrderValue: number;
}

export interface ISalesData {
  SalesLast3Months: number;
  SalesLast30Days: number;
  SalesYearToDay: number;
}
